
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheTable from '@/common/components/UI/TheTable/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'
import PageTitle from '@/common/components/UI/PageTitle/index.vue'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import { BillingDeliveryCompany } from '@/core/types/delivery-companies.types'

import {
    DeliveryIndividualShipmentCostPayload,
    DeliveryIndividualStandardParamsPayload,
} from '@/core/types/delivery-shipment-costs.types'

import {
    TariffIndividualT,
    TariffIndividualParamT,
    TariffIndividualAdditionalServiceT,
} from '@/core/types/billing.types'

// realisations
import { deliveryCompanies } from '@/core/realisations/delivery-companies.realisation'
import { deliveryShipmentCosts } from '@/core/realisations/delivery-shipment-costs.realisation'

// validations
import useVuelidate from '@vuelidate/core'
import { helpers, required, decimal } from '@vuelidate/validators'

// constants
import {
    IndividualTariffsMain,
    IndividualTariffs,
    StandardParamsColumns,
    StandardParamsRows,
    AdditionalServicesColumns,
} from '@/core/constants/columns/IndividualTariffs.columns'

import {
    INDIVIDUAL_PERCENT_LIST,
    INDIVIDUAL_PRICE_LIST,
    UNIT_IDS_1,
    UNIT_IDS_3,
    OVERLOAD_WEIGHT_TYPES,
    INDIVIDUAL_ADDITIONAL_SERVICES,
} from '@/core/constants/billing.constants'

import { AdditionalServicesEnum } from '@/core/enums/billing.enums'

// composable
import useNotifications from '@/common/composable/useNotifications'

export default defineComponent({
    components: {
        TheButton,
        TheSelect,
        TheTable,
        TheInput,
        TableScrollWrapper,
        PageTitle,
    },
    setup() {
        const notifications = useNotifications()

        const state = reactive({
            is_loading: false,
            //
            companyId: null as number | null,
            shipmentCosts: null as TariffIndividualT[] | null,
            standardParams: null as TariffIndividualParamT[] | null,
            //
            IDS_SPECIAL: [
                { typeId: 1, ids: [5, 11, 23] },
                { typeId: 2, ids: [16, 17, 28, 29] },
            ],
            additionalServices:
                INDIVIDUAL_ADDITIONAL_SERVICES as TariffIndividualAdditionalServiceT[],
        })

        const state_form$ = useVuelidate(
            {
                companyId: { required },
                // @ts-ignore
                shipmentCosts: { $each: helpers.forEach({ minPrice: { required, decimal } }) },
                // @ts-ignore
                standardParams: { $each: helpers.forEach({ amount: { required, decimal } }) },
                additionalServices: {
                    // @ts-ignore
                    $each: helpers.forEach({
                        minValue: { decimal },
                        price: { required, decimal },
                    }),
                },
            },
            state
        )

        const data_state = reactive({
            companies: new DataState<BillingDeliveryCompany>({}),
        })

        const loadCompanies = async () => {
            try {
                data_state.companies.is_loading = true

                const response = await deliveryCompanies.getBillingCompanies({})
                data_state.companies.list = response.data

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const editShipmentCosts = () => {
            if (!state.shipmentCosts) return

            let shipment_costs: TariffIndividualT[] = [...state.shipmentCosts]

            shipment_costs = shipment_costs.map((s_c, i) => {
                let s_c_tmp: TariffIndividualT = { ...s_c }

                let num = state.standardParams?.find(
                    (obj) => obj.name === 'overloadWeight' && obj.typeId === s_c_tmp.typeId
                )

                if (num && num.amount) {
                    if (s_c_tmp.weight === null || s_c_tmp.weight > num.amount) {
                        let s_found = shipment_costs.find(
                            (s) =>
                                s.weight === num?.amount &&
                                s.typeId === num?.typeId &&
                                s.zoneId === s_c_tmp.zoneId
                        )

                        s_c_tmp.cost = Number(s_c_tmp.minPrice)
                        if (s_found) s_c_tmp.minPrice = Number(s_found.minPrice)
                    } else {
                        s_c_tmp.cost = 0
                        s_c_tmp.minPrice = Number(s_c_tmp.minPrice)
                    }
                }

                return s_c_tmp
            })

            return shipment_costs
        }

        const addStandardParams = async () => {
            try {
                let payload: DeliveryIndividualStandardParamsPayload = {
                    deliveryCompanyId: state.companyId as number,
                    deliveryStandardParams: state.standardParams as TariffIndividualParamT[],
                }

                state.is_loading = true

                await deliveryShipmentCosts.createIndividualStandardParams(payload)

                notifications.notify({ title: 'Тариф успешно создан!', type: 'success' })
            } catch (error) {
                console.error(error)
                notifications.notify({
                    title: 'При создании тарифа произошла ошибка!',
                    type: 'danger',
                })
            } finally {
                state.is_loading = false
            }
        }

        // const addAdditionalServices = async () => {
        //     try {
        //         let additional_services = state.additionalServices.map((service) => {
        //             return {
        //                 ...service,
        //                 price: Number(service.price),
        //                 minValue: Number(service.minValue),
        //             }
        //         })

        //         let payload: IndividualTariffAdditionalServicesPayloadT = {
        //             companyId: state.companyId as number,
        //             additionalServices: additional_services as TariffIndividualAdditionalServiceT[],
        //         }

        //         state.is_loading = true

        //         await billing.addAdditionalServices(payload)

        //         notifications.notify({ title: 'Доп. услуги внесены!', type: 'success' })
        //         // await router.push('/tariffs/individual')
        //     } catch (error) {
        //         console.error(error)
        //         notifications.notify({
        //             title: 'При внесении доп. услуг произошла ошибка!',
        //             type: 'danger',
        //         })
        //     } finally {
        //         state.is_loading = false
        //     }
        // }

        const onSubmit = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            try {
                let shipment_costs_edited = editShipmentCosts()

                let payload: DeliveryIndividualShipmentCostPayload = {
                    deliveryCompanyId: state.companyId as number,
                    deliveryShipmentCosts: shipment_costs_edited as TariffIndividualT[],
                }

                state.is_loading = true

                await deliveryShipmentCosts.createIndividualShipmentCost(payload)
                await addStandardParams()

                notifications.notify({ title: 'Тариф успешно создан!', type: 'success' })
                // await addAdditionalServices()
            } catch (error) {
                console.error(error)
                notifications.notify({
                    title: 'При создании тарифа произошла ошибка!',
                    type: 'danger',
                })
            } finally {
                state.is_loading = false
            }
        }

        const fillInitialArray = () => {
            let s_c_blank = { zoneId: 1, typeId: 1, weight: null, minPrice: null, cost: 0 }

            state.shipmentCosts = [...new Array(30).fill({ ...s_c_blank })]

            state.shipmentCosts = state.shipmentCosts.map((s_c, i) => {
                let s_c_tmp: TariffIndividualT = { ...s_c }

                // zoneId
                if (5 < i && i < 18) {
                    s_c_tmp.zoneId = 2
                } else if (i > 17) {
                    s_c_tmp.zoneId = 3
                }

                // typeId
                if ((11 < i && i < 18) || i > 23) s_c_tmp.typeId = 2

                // weight
                if (i % 6 === 0) s_c_tmp.weight = 0.5
                else if (i % 6 === 1) s_c_tmp.weight = 1
                else if (i % 6 === 2) s_c_tmp.weight = 2
                else if (i % 6 === 3) s_c_tmp.weight = 5
                else if (i % 6 === 4) s_c_tmp.weight = 10

                return s_c_tmp
            })
        }

        const fillParamsArray = () => {
            let params_blank: TariffIndividualParamT = {
                name: '',
                description: '',
                amount: null,
                unitId: 2,
                typeId: 1,
            }

            state.standardParams = [...new Array(26).fill({ ...params_blank })]

            state.standardParams = state.standardParams.map((param, i) => {
                let param_tmp: TariffIndividualParamT = { ...param }
                let index_row = Math.floor(i / 2)

                // name & description
                param_tmp.name = StandardParamsRows[index_row].name
                param_tmp.description = StandardParamsRows[index_row].description

                // type_id
                param_tmp.typeId = (i % 2) + 1

                // amount
                if (param_tmp.name === 'overloadWeight') {
                    param_tmp.amount = param_tmp.typeId === 1 ? 10 : 5
                }

                // amount
                if (param_tmp.name === 'cubSize') {
                    param_tmp.amount = 200
                }

                // unit_id
                if (UNIT_IDS_1.includes(param_tmp.name)) {
                    param_tmp.unitId = 1
                } else if (UNIT_IDS_3.includes(param_tmp.name)) {
                    param_tmp.unitId = 3
                }

                return param_tmp
            })
        }

        const getIndex = (key: string) => {
            let key_arr: string[] = key.split('-')

            let key_right = Number(key_arr[1])
            let key_left = Number(key_arr[0])

            return key_left + (key_right - 1) * 6 - 1
        }

        const getIndexTitle = (key: string) => {
            let key_arr: string[] = key.split('-')

            let key_right = Number(key_arr[1])

            return key_right - 1
        }

        const getRowIndex = (key: string) => {
            return key.split('-')[0]
        }

        const getParamsIndex = (key: string) => {
            let key_arr: string[] = key.split('-')

            let key_right = Number(key_arr[1])
            let key_left = Number(key_arr[0])

            let index = key_left + (key_right - 1) * 13

            return index < 13 ? index * 2 : index - (26 - index) + 1
        }

        const getPlaceholder = (index: number, typeId: number) => {
            if (!typeId) return

            let ids_obj = state.IDS_SPECIAL.find((obj) => obj.typeId === typeId)

            return ids_obj && ids_obj.ids.includes(index) ? 'Цена (за кг)' : 'Цена'
        }

        const onParamsInput = (index: number) => {
            if (state.standardParams && index % 2 === 0) {
                state.standardParams[index + 1].amount = state.standardParams[index].amount
            }
        }

        const getSign = (name: string) => {
            if (INDIVIDUAL_PERCENT_LIST.includes(name)) return '(%)'
            else if (INDIVIDUAL_PRICE_LIST.includes(name)) return '(₸)'
            else if (name === 'overloadWeight') return '(кг)'
            else return ''
        }

        const onOverloadChange = (value: number, typeId: number) => {
            let ids_obj = state.IDS_SPECIAL.find((obj) => obj.typeId === typeId)

            if (!ids_obj) return

            ids_obj.ids = []

            state.shipmentCosts?.forEach((s_c, idx) => {
                if (
                    ids_obj &&
                    s_c.typeId === typeId &&
                    (s_c.weight === null || s_c.weight > value)
                ) {
                    ids_obj.ids.push(idx)
                }
            })
        }

        onBeforeMount(() => {
            loadCompanies()
            fillInitialArray()
            fillParamsArray()
        })

        return {
            state,
            data_state,
            //
            state_form$,
            //
            onSubmit,
            //
            loadCompanies,
            //
            IndividualTariffsMain,
            IndividualTariffs,
            StandardParamsColumns,
            AdditionalServicesColumns,
            StandardParamsRows,
            //
            getIndex,
            getIndexTitle,
            getRowIndex,
            getPlaceholder,
            //
            getParamsIndex,
            onParamsInput,
            getSign,
            onOverloadChange,
            //
            OVERLOAD_WEIGHT_TYPES,
            AdditionalServicesEnum,
        }
    },
})
